import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { storeToRefs } from 'pinia';

import { useSocketMessage } from '@/composables/domains/chat/socket/useSocketMessage';
import { useMic } from '@/composables/shared/useMic';
import { useNotification } from '@/composables/shared/useNotification';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { useChatStore } from '@/stores/chat';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast';
import { UseMessagesApecOptions } from '@/types/composables';
import {
  ChatMessageSingle,
  MessageAm,
  MessageFlag,
  MessageHistory,
  MessageLd,
  MessageMap,
  MessageText,
  MessageTr,
  MessageTs,
  MessageCd,
} from '@/types/socket';
import { isLanguageNoSpace } from '@/utils/chat/language';
import { base64UrlSafeToString } from '@/utils/encryptor.js';
import { SocketState } from '@/utils/enums';

let instance = null as ReturnType<typeof useMessagesSingle> | null; // 싱글톤 인스턴스

export function useMessagesSingle(options?: UseMessagesApecOptions) {
  // CallContents, socketStore 에서 공용되기 때문에 싱글톤 패턴 강제
  if (!instance) {
    if (!options) {
      throw new Error('useMessagesSingle must be initialized with options before use.');
    }
    instance = createMessagesSingleInstance(options);
  }
  return instance;
}

// 인스턴스 생성자
function createMessagesSingleInstance({
  isGuestMicMuted,
  attendees,
  recover,
  isFastInterpretation,
  selectedChatId,
  connectionResponse,
  socketState,
  setAttendees,
  findAttendeeNickname,
  sendMessageAttendees,
}: UseMessagesApecOptions) {
  const audioStore = useAudioStore();
  const callStore = useCallStore();
  const chatStore = useChatStore();
  const socketStore = useSocketStore();
  const router = useRouter();
  const toastStore = useToastStore();
  const { t } = useI18n();
  const { micStop } = useMic();
  const { alertAccessSimultaneous, alertSessionEnded } = useNotification();
  const { handleMessageUE, handleMessageUT } = useSocketMessage();

  const { languageDetected, isMutedByEmployee, isMicOn, languageHome, languageAway } = storeToRefs(audioStore);
  const { qrCodeInfo } = storeToRefs(callStore);

  const prevMyMessageId = ref<string>('');
  const prevOtherMessageId = ref<string>('');
  const emptyBubbleCallLogIdMy = ref<string>('');
  const emptyBubbleCallLogIdOther = ref<string>('');
  const messageMap = ref<MessageMap>({}); // 수신된 메세지 모듬 정제 1단계
  const messageList = ref<ChatMessageSingle[]>([]); // 수신된 메세지 모듬 정제 2단계
  const messageBank = ref([]);
  const dateMessageLast = ref<string>('');
  const isLoadingMessagePast = ref<boolean>(false);
  const hasMoreMessagePast = ref<boolean | null>(null);

  // 메세지 처리
  const handleWebSocketMessage = async (data) => {
    messageBank.value.push(data); // 디버깅 용도
    let flag: MessageFlag = data.slice(0, 2) as MessageFlag;

    if (['sw', 'pi', 'tc', 'cc'].includes(flag)) {
      return;
    }

    if (data.length <= 10) return;

    const message = JSON.parse(data.substring(10));
    const isOwn = message.senderId === localStorage.getItem('userIdConnected');

    switch (flag) {
      case 'sl':
        return handleMessageSl(message);
      case 'al':
        return setAttendees(message.attendees);
      case 'ca':
        return handleMessageCa(message);
      case 'at':
        return handleMessageAt(message);
      case 'ld':
        return handleMessageLd(message);
      case 'ta':
        await handleMessageTa();
        break;
      case 'ue':
        return handleMessageUE();
      case 'ut':
        return handleMessageUT(message);
    }

    //ts 처리
    if (data instanceof ArrayBuffer) {
      data = new TextDecoder().decode(data);
    }

    if (typeof data === 'string') {
      flag = data.slice(0, 2) as MessageFlag;
      const length = parseInt(data.slice(2, 10), 10);

      if (data.length < 10 + length) {
        throw new Error('Data length mismatch');
      }

      switch (flag) {
        case 'ts':
          handleMessageTs(message, isOwn);
          break;
        case 'am':
          return handleMessageAm(message);
        case 'fi':
          isFastInterpretation.value = message.isFastInterpretationOn === '1' ? true : false;
          return;
        case 'tr':
          handleMessageTr(message);
          break;
        case 'er':
          return handleMessageEr(message);
        case 'td':
          recover.value = true;
          return;
        case 'cd':
          return handleMessageCd(message);
      }
    }
  };

  // 메세지 맵 초기화
  const initializeMessageMap = (
    callLogId: string,
    nickname: string,
    senderId: string,
    sentAt: number,
    reset: '0' | '1',
    languageCode: string
  ) => {
    if (!messageMap.value[callLogId]) {
      const mapNew = {
        sentAt: sentAt ?? new Date(),
        senderId: senderId,
        nickname: nickname,
        reset: reset,
        timer: 0,
      };
      messageMap.value[callLogId] = mapNew;
      const codeHome = languageHome.value.code;
      const codeAway = languageAway.value.code;
      const defaultContent = () => ({ contents: [], isComplete: false });
      messageMap.value[callLogId][languageCode] = defaultContent();
      messageMap.value[callLogId][codeHome] = defaultContent();
      messageMap.value[callLogId][codeAway] = defaultContent();
      messageMap.value[callLogId][`${codeHome}History`] = {};
      messageMap.value[callLogId][`${codeAway}History`] = {};
    }
  };

  // 메세지 배열 갱신
  const updateMessageList = (callLogId: string, flag?: MessageFlag): void => {
    const data = messageMap.value[callLogId];

    const { sentAt = 0, inout, senderId, userId, nickname, reset, state } = data;

    const isOwn = senderId === localStorage.getItem('userIdConnected');
    const time = new Date(sentAt).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const message: ChatMessageSingle = {
      callLogId,
      autoPlay: true,
      senderId: senderId || userId || '',
      sentAt,
      inout,
      isOwn,
      time,
      nickname,
      flag,
      reset,
      state,
    };

    if (languageHome.value.code in data) {
      const langCode = languageHome.value.code;
      message[langCode] = {
        contents: data[langCode].contents ?? [],
        isComplete: data[langCode].isComplete ?? false,
      };
      message[`${langCode}AudioKeys`] = data[langCode].audioKey ?? [];
    }
    if (languageAway.value.code in data) {
      const langCode = languageAway.value.code;
      message[langCode] = {
        contents: data[langCode].contents ?? [],
        isComplete: data[langCode].isComplete ?? false,
      };
      message[`${langCode}AudioKeys`] = data[langCode].audioKey ?? [];
    }

    const inx: number = messageList.value.findIndex((itemMessage) => itemMessage.callLogId === callLogId);

    if (inx !== -1) {
      messageList.value[inx] = { ...messageList.value[inx], ...message };
    } else {
      messageList.value.push(message);
    }

    messageList.value = [...messageList.value]; // force reactivity
  };

  const setEmptyBubbleMy = (callLogId: string) => {
    emptyBubbleCallLogIdMy.value = callLogId;
  };

  const setEmptyBubbleOther = (callLogId: string) => {
    emptyBubbleCallLogIdOther.value = callLogId;
  };

  // 빈 챗 버블(...) 없애기
  const popBubbleEmpty = (callLogId) => {
    const popTargetIndex = messageList.value.findIndex((message) => message.callLogId === callLogId);
    messageList.value.splice(popTargetIndex, 1);
    messageList.value = [...messageList.value]; // DOM update 강제
    if (emptyBubbleCallLogIdMy.value === callLogId) {
      emptyBubbleCallLogIdMy.value = '';
    } else {
      emptyBubbleCallLogIdOther.value = '';
    }
  };

  //기존 말풍선 ... 제거
  const turnOffTs = (mMessageIdRef) => {
    const index = messageList.value.findIndex((msg) => msg.callLogId === mMessageIdRef.value);
    if (index !== -1) {
      messageList.value[index].showLoading = false;
    }
  };

  // 연결 성공
  const handleMessageCa = ({ chatId }) => {
    localStorage.setItem('userIdConnected', localStorage.getItem('userId') ?? '');

    selectedChatId.value = chatId;
    connectionResponse.value = 2;
    if (socketStore.connectInfo!.connectionType === 'GENERAL') {
      router.push({
        name: 'chat-general',
        params: { code: socketStore.connectInfo.code },
      });
    }
  };

  // 단일 언어 여부 메세지
  const handleMessageSl = ({ isSingleLanguage }) => {
    audioStore.isSingleLanguage = isSingleLanguage;
  };

  // 참석자 정보
  const handleMessageAt = ({ count }) => {
    if (attendees.value.size !== count) {
      sendMessageAttendees();
    }
  };

  // 언어 감지
  const handleMessageLd = (messageLd: MessageLd) => {
    languageDetected.value = messageLd;
  };

  // 마이크 음소거
  const handleMessageAm = ({ isMuteOn }: MessageAm) => {
    const isOn = isMuteOn === '1';
    isGuestMicMuted.value = isOn;
    if (qrCodeInfo.value.qrCodeType === 'GENERAL_CALL_PARTNER') return;
    isMutedByEmployee.value = isOn;
    if (isOn) isMicOn.value = false;
  };

  // 통역중
  const handleMessageTs = (messageTs: MessageTs, isOwn: boolean) => {
    const { callLogId, reset } = messageTs;
    if (messageMap.value[callLogId]) {
      messageMap.value[callLogId].reset = reset;
    }
    if (isOwn) {
      //...말풍선 (새로운) 위치 저장
      prevMyMessageId.value = callLogId;
    } else {
      prevOtherMessageId.value = callLogId;
    }
    if (reset === '1') {
      //reset 값 1이면 off
      turnOffTs(isOwn ? prevMyMessageId : prevOtherMessageId);
    } else {
      // turnOnTs(isOwn ? prevMyMessageId : prevOtherMessageId, messageTs);
    }
  };

  // 통역내용
  const handleMessageTr = async (messageTr: MessageTr) => {
    const {
      callLogId,
      isAudioComplete,
      dataFlag,
      isTextComplete,
      message,
      nickname,
      sentAt,
      senderId,
      reset,
      chatSeqSubId,
      logFlag,
      isComplete,
      isFastInterpretationOn,
      languageCode,
      voiceKey,
    } = messageTr;

    const indexMessage = messageList.value.findIndex((msg) => msg.callLogId === callLogId);

    if (indexMessage !== -1) {
      messageList.value[indexMessage].isAudioComplete = isAudioComplete;
      // 오디오 수신 완료 시 showLoading 강제 False 처리
      if (isAudioComplete && messageList.value[indexMessage].showLoading) {
        messageList.value[indexMessage].showLoading = false;
        messageList.value[indexMessage].reset = '1';
      }
    }

    const nicknameDecoded = base64UrlSafeToString(nickname) ?? findAttendeeNickname(senderId!);

    initializeMessageMap(callLogId, nicknameDecoded, senderId ?? '', sentAt, reset, languageCode);

    if (!isAudioComplete) {
      console.log(
        `[TEXT][${languageCode}] id: ${callLogId} ${chatSeqSubId} ${logFlag}, text/isComplete: ${isTextComplete} ${isComplete} (${isFastInterpretationOn}) ${senderId} msg: ${message}`
      );
    } else if (isAudioComplete) {
      console.log(
        `[AUDIO] ${languageCode} callLogId: ${callLogId}, subId: ${chatSeqSubId}, completed:${isTextComplete}/${isAudioComplete}, msg: ${message}, vUrl: ${voiceKey}`
      );
    }

    // 이미 완료된 메세지는 더 어상 수정하지 않음
    if (
      messageMap.value[callLogId]?.[`${languageCode}History`]?.[chatSeqSubId]?.thoroughlyCompletedContent?.length > 0 &&
      !voiceKey
    ) {
      console.error('앞의 내용이 누락되지 않고 이 메세지가 없다면 해결 완료:', message, messageTr);
      return;
    }

    if (sentAt <= 0) return;

    if (!['src', 'tgt'].includes(dataFlag)) return;

    const historyKey = `${languageCode}History`;

    messageMap.value[callLogId][languageCode] ??= {};
    messageMap.value[callLogId][historyKey] ??= {};
    messageMap.value[callLogId][languageCode].isComplete = isComplete;
    messageMap.value[callLogId][languageCode].isAudioComplete = isAudioComplete;

    if (isAudioComplete) {
      messageMap.value[callLogId][languageCode] ??= {};
      const existingAudioKeys = messageMap.value[callLogId][languageCode]?.audioKey ?? [];

      const newAudioEntry = voiceKey
        ? {
            chatId: selectedChatId.value,
            chatSeqSubId: chatSeqSubId,
            voiceKey: voiceKey,
            callLogId: callLogId,
            logFlag: logFlag,
          }
        : null;

      messageMap.value[callLogId][languageCode].audioKey = newAudioEntry
        ? [...existingAudioKeys, newAudioEntry]
        : existingAudioKeys;
      updateMessageList(callLogId, 'tr');
      return;
    }

    handleMessageFiOn(messageTr, historyKey);

    const removeEmptyBubble = (bubbleId: string, currentMessageId: string) => {
      if (bubbleId && bubbleId !== currentMessageId) popBubbleEmpty(bubbleId);
    };
    removeEmptyBubble(emptyBubbleCallLogIdMy.value, callLogId);
    removeEmptyBubble(emptyBubbleCallLogIdOther.value, callLogId);

    updateMessageList(callLogId, 'tr');
  };

  // 마이크 종료 메시지
  const handleMessageTa = async () => {
    toastStore.warn(t('auto_mic_off'));
    micStop();
    isMicOn.value = false;
  };

  // DM 메세지 삭제
  const deleteMessageMt = (socketId: string) => {
    const inx = messageList.value.findIndex(({ callLogId }) => callLogId === socketId);

    if (inx >= 0) {
      messageList.value.splice(inx, 1);
      delete messageMap.value[socketId];
      chatStore.deleteErrorChat(selectedChatId.value, socketId);
    }
  };

  // 에러 발생
  const handleMessageEr = ({ code, errorCode }) => {
    if (code === '2002') socketState.value = SocketState.ERROR_NO_HEADER;
    connectionResponse.value = errorCode;
  };

  // 동시통역
  const handleMessageFiOn = (messageTr: MessageTr, historyKey: string) => {
    const { callLogId, chatSeqSubId, message, incompleteText, isTextComplete, isComplete, logFlag, languageCode } =
      messageTr;

    const history = (messageMap.value[callLogId][historyKey][chatSeqSubId] ??= {
      completedContents: [],
      creatingContent: null,
    });
    const newMessage = { message, logFlag };

    if (isTextComplete || isComplete) {
      if (isComplete && message.trim() === '' && !history.creatingContent) {
        return;
      }

      if (isComplete) {
        history.thoroughlyCompletedContent = [...history.completedContents, newMessage];
        history.key = Date.now();
        history.completedContents = [];
        history.creatingContent = null;
      } else {
        history.completedContents.push(newMessage);
      }

      history.creatingContent = {
        message: incompleteText,
        logFlag,
      };
    } else {
      if (!history.thoroughlyCompletedContent) {
        history.creatingContent = newMessage;
      }
    }

    messageMap.value[callLogId][languageCode] ??= {};
    messageMap.value[callLogId][languageCode].contents = Object.entries(
      messageMap.value[callLogId][historyKey] as Record<string, MessageHistory>
    )
      .flatMap(([key, value], index, array) => {
        const isLast = index === array.length - 1;
        const isFirst = index === 0;
        const result: Array<MessageText> = [];
        const isNoSpaceLang = isLanguageNoSpace(languageCode);
        const splitter = isNoSpaceLang ? '' : ' ';

        if (value.thoroughlyCompletedContent) {
          let contentParsed = value.thoroughlyCompletedContent.map((content) => content.message).join(splitter);
          if (isNoSpaceLang) contentParsed = contentParsed.replaceAll(' ', '');
          if (!isFirst) contentParsed = splitter + contentParsed;

          result.push({
            highlight: false,
            content: contentParsed,
            fadeout: true,
            key: value.key,
            isThoroughlyComplete: true,
            messageEdited: value.messageEdited,
            chatSeqSubId: key,
            isEditable: true,
            logFlag,
          });
        } else if (value.completedContents.length > 0) {
          let contentParsed = value.completedContents.map((content) => content.message).join(splitter);
          if (!isFirst) contentParsed = splitter + contentParsed;

          result.push({
            highlight: false,
            content: contentParsed,
            fadeout: isLast,
            key: Date.now(),
            isThoroughlyComplete: false,
            messageEdited: value.messageEdited,
            chatSeqSubId: key,
            logFlag,
          });
        }

        if (value.creatingContent?.message?.trim?.()) {
          result.push({
            highlight: true,
            content: `${splitter}${value.creatingContent.message}`,
            fadeout: false,
            key: Date.now(),
            isThoroughlyComplete: false,
            messageEdited: value.messageEdited,
            chatSeqSubId: key,
            logFlag,
          });
        }

        return result;
      })
      .sort((a, b) => parseInt(a.chatSeqSubId ?? '0') - parseInt(b.chatSeqSubId ?? '0'));
  };

  // 퇴장 메세지
  const handleMessageCd = (messageCd: MessageCd) => {
    const { userId, reasonCode } = messageCd;

    // 나일 경우
    if (userId === localStorage.getItem('userIdConnected')) {
      switch (reasonCode) {
        case '1': // 동시접속?
          alertAccessSimultaneous();
          return false;
        case '3': // 세션 종료
          alertSessionEnded();
          return false;
      }
    }
  };

  // 이 composable의 모든 상태를 초기화
  const resetUseMessagesCall = () => {
    messageMap.value = {};
    messageList.value = [];
    prevMyMessageId.value = '';
    prevOtherMessageId.value = '';
    emptyBubbleCallLogIdMy.value = '';
    emptyBubbleCallLogIdOther.value = '';
    dateMessageLast.value = '';
  };

  return {
    messageMap,
    messageList,
    messageBank,
    prevMyMessageId,
    hasMoreMessagePast,
    prevOtherMessageId,
    emptyBubbleCallLogIdMy,
    emptyBubbleCallLogIdOther,
    isLoadingMessagePast,
    handleWebSocketMessage,
    popBubbleEmpty,
    setEmptyBubbleMy,
    setEmptyBubbleOther,
    resetUseMessagesCall,
    deleteMessageMt,
  };
}
